import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { cognitoAuth } from '@/services/cognitoAuth';
import { sessionStorageService } from '@/services/sessionStorageService';
import type { CognitoUser } from '@/types';
import { logger } from '@/utils/logger';

interface CustomerAuthContextType {
  user: CognitoUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasPendingData: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, companyName: string) => Promise<{ confirmationRequired: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => void;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

interface CustomerAuthProviderProps {
  children: ReactNode;
}

export const CustomerAuthProvider: React.FC<CustomerAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPendingData, setHasPendingData] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        const currentUser = cognitoAuth.getCurrentUser();
        setUser(currentUser);

        // Check for pending data
        const pendingDataExists = sessionStorageService.hasPendingData();
        setHasPendingData(pendingDataExists);

        logger.info('Customer auth initialized', {
          isAuthenticated: !!currentUser,
          hasPendingData: pendingDataExists
        });
      } catch (error) {
        logger.error('Failed to check auth status:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      logger.info('Customer signing in', { email });
      const result = await cognitoAuth.signIn(email, password);

      // If password challenge is required, throw error so LoginForm can handle it
      if ('requiresNewPassword' in result) {
        throw new Error('Password change required - handle in LoginForm');
      }

      setUser(result);

      // Note: Pending data persistence is handled in the respective components
      // after login completes, to avoid circular dependencies

      logger.info('Customer signed in successfully', { userId: result.userId });
    } catch (error) {
      logger.error('Sign in failed:', error);
      throw error;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    companyName: string
  ): Promise<{ confirmationRequired: boolean }> => {
    try {
      logger.info('Customer signing up', { email });
      const result = await cognitoAuth.signUp(email, password, name, companyName);

      logger.info('Customer signed up successfully', {
        userId: result.userId,
        confirmationRequired: result.confirmationRequired
      });

      return result;
    } catch (error) {
      logger.error('Sign up failed:', error);
      throw error;
    }
  };

  const confirmSignUp = async (email: string, code: string): Promise<void> => {
    try {
      logger.info('Confirming customer signup', { email });
      await cognitoAuth.confirmSignUp(email, code);
      logger.info('Customer signup confirmed', { email });
    } catch (error) {
      logger.error('Signup confirmation failed:', error);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      logger.info('Customer signing out');
      await cognitoAuth.signOut();
      setUser(null);

      // Don't clear pending data on logout - user might want to sign back in
      logger.info('Customer signed out successfully');
    } catch (error) {
      logger.error('Sign out failed:', error);
      throw error;
    }
  };

  const refreshUser = (): void => {
    const currentUser = cognitoAuth.getCurrentUser();
    setUser(currentUser);

    const pendingDataExists = sessionStorageService.hasPendingData();
    setHasPendingData(pendingDataExists);
  };

  const value: CustomerAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    hasPendingData,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    refreshUser,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCustomerAuth = (): CustomerAuthContextType => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
